import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { format, isValid, parse, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { CalendarIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '../primitives/Button'
import { Calendar } from '../primitives/Calendar'
import { Label } from '../primitives/Label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface FormDatePickerProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
  rules?: Record<string, unknown>
  disabled?: boolean
  className?: string
  showLabel?: boolean
  minDate?: Date
  maxDate?: Date
}

// Date format for display
const DISPLAY_DATE_FORMAT = 'd MMM yyyy'
// Date format for storing (YYYY-MM-DD)
const STORAGE_DATE_FORMAT = 'yyyy-MM-dd'

// Helper to parse date from string
const parseDateFromString = (
  dateString: string | Date | null | undefined
): Date | undefined => {
  if (!dateString) return undefined

  // If it's already a Date object
  if (dateString instanceof Date) {
    return isValid(dateString) ? dateString : undefined
  }

  try {
    // Try parsing as ISO format first
    const isoDate = parseISO(dateString)
    if (isValid(isoDate)) return isoDate
    // Try parsing as storage format
    const date = parse(dateString, STORAGE_DATE_FORMAT, new Date())
    return isValid(date) ? date : undefined
  } catch {
    return undefined
  }
}

// Helper to format date for storage
const formatDateForStorage = (date: Date | undefined): string => {
  if (!date || !isValid(date)) return ''
  return format(date, STORAGE_DATE_FORMAT)
}

export function FormDatePicker<T extends FieldValues>({
  control,
  name,
  label,
  placeholder = 'Seleccione una fecha',
  rules,
  disabled = false,
  className,
  showLabel = true,
  minDate,
  maxDate,
}: FormDatePickerProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedDate = parseDateFromString(field.value as string | Date)

        const handleDateSelect = (date: Date | undefined) => {
          const formattedDate = formatDateForStorage(date)
          field.onChange(formattedDate)
        }

        return (
          <div className={cn('flex flex-col gap-2', className)}>
            {showLabel && label && (
              <Label htmlFor={name} required={!!rules?.required}>
                {label}
              </Label>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  className={cn(
                    'h-9 w-full justify-start px-3 font-normal text-[#262626]',
                    'border-[#D4D4D4] bg-[#FAFAFA] text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
                    'hover:bg-[#F5F5F5] active:bg-[#E5E5E5]',
                    !selectedDate && 'text-[#737373]',
                    fieldState.error && 'border-red-500'
                  )}
                  disabled={disabled}
                  variant="outline"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? (
                    format(selectedDate, DISPLAY_DATE_FORMAT, { locale: es })
                  ) : (
                    <span>{placeholder}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className={cn(
                  'w-[calc(100vw-16px)] max-w-[240px] rounded-md border border-[#D4D4D4]',
                  'bg-[#FAFAFA] p-0 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]',
                  'sm:w-auto'
                )}
                side="bottom"
                sideOffset={4}
              >
                <div className="flex flex-col items-center gap-3 p-2 sm:gap-4">
                  <Calendar
                    className="p-0"
                    defaultMonth={selectedDate || new Date()}
                    disabled={(date) => {
                      if (disabled) return true
                      if (minDate && date < minDate) return true
                      if (maxDate && date > maxDate) return true
                      return false
                    }}
                    locale={es}
                    onSelect={handleDateSelect}
                    selected={selectedDate}
                  />

                  {/* Clear Button */}
                  <Button
                    className={cn(
                      'h-9 w-full min-w-[36px] justify-center rounded-md px-4',
                      'border-0 bg-[#FAFAFA] shadow-none',
                      'hover:bg-[#F5F5F5] active:bg-[#FAFAFA]'
                    )}
                    onClick={() => handleDateSelect(undefined)}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    <span className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                      Limpiar selección
                    </span>
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            {fieldState.error && (
              <span className="text-xs text-red-600">
                {fieldState.error.message}
              </span>
            )}
          </div>
        )
      }}
      rules={rules}
    />
  )
}
